import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { studentMasterAPI } from '../../services/api';
import './Modal.css';

const EditStudentModal = ({ student, onClose, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('info'); // 'info' or 'enrollment'
  const [isEditingEnrollment, setIsEditingEnrollment] = useState(false);

  const [formData, setFormData] = useState({
    id: student.id,
    name: student.name || '',
    dob: student.dob ? new Date(student.dob).toISOString().split('T')[0] : '',
    jantina: student.jantina || '',
    citizenStatus: student.citizenStatus || ''
  });

  const [enrollmentData, setEnrollmentData] = useState({
    enrollYear: student.enrollYear || null,
    tahun: student.tahun || '',
    kelas: student.kelas || '',
    status: student.enrollmentStatus || 'ACTIVE'
  });

  const [tahunsList, setTahunsList] = useState([]);
  const [kelasList, setKelasList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Format IC for display with dashes (XXXXXX-XX-XXXX)
  // Backend already handles masking based on permissions
  const formatIC = (ic) => {
    if (!ic) return '-';
    if (ic.length < 4) return ic; // Already masked (last 4 digits only)
    if (ic.length === 12) {
      // Format 12-digit IC: XXXXXX-XX-XXXX
      return `${ic.substring(0, 6)}-${ic.substring(6, 8)}-${ic.substring(8)}`;
    }
    // For any other format, return as-is
    return ic;
  };

  // Load tahun and kelas lists
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        // Load tahun list
        const tahunResponse = await studentMasterAPI.getTahuns();
        if (tahunResponse.success) {
          setTahunsList(tahunResponse.data || []);
        }

        // Load kelas list for current tahun
        if (enrollmentData.tahun) {
          const kelasResponse = await studentMasterAPI.getKelas(null, enrollmentData.tahun);
          if (kelasResponse.success) {
            setKelasList(kelasResponse.data || []);
          }
        }
      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    };

    if (activeTab === 'enrollment' && isEditingEnrollment) {
      loadDropdownData();
    }
  }, [activeTab, isEditingEnrollment, enrollmentData.tahun]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEnrollmentChange = async (field, value) => {
    setEnrollmentData(prev => ({ ...prev, [field]: value }));

    // If tahun changes, reload kelas list
    if (field === 'tahun') {
      try {
        const kelasResponse = await studentMasterAPI.getKelas(null, value);
        if (kelasResponse.success) {
          setKelasList(kelasResponse.data || []);
          // Reset kelas selection
          setEnrollmentData(prev => ({ ...prev, kelas: '' }));
        }
      } catch (error) {
        console.error('Error loading kelas list:', error);
      }
    }
  };

  const handleSaveEnrollment = async () => {
    setIsSubmitting(true);
    try {
      const response = await studentMasterAPI.updateEnrollment(student.id, {
        enrollYear: enrollmentData.enrollYear,
        tahun: enrollmentData.tahun,
        kelas: enrollmentData.kelas,
        updateUniformData: true
      });

      if (response.success) {
        Swal.fire('Berjaya', 'Maklumat pendaftaran berjaya dikemaskini', 'success');
        setIsEditingEnrollment(false);
        onSuccess();
      } else {
        Swal.fire('Gagal', response.message || 'Gagal mengemaskini pendaftaran', 'error');
      }
    } catch (error) {
      console.error('Update enrollment error:', error);
      Swal.fire('Ralat', 'Ralat semasa mengemaskini pendaftaran', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStatusChange = async (action) => {
    const statusMap = {
      'TRANSFERRED': { title: 'Transfer Keluar', reason: 'TRANSFERRED', message: 'Pelajar akan ditandakan sebagai pindah sekolah' },
      'GRADUATED': { title: 'Tamat Pengajian', reason: 'GRADUATED', message: 'Pelajar akan ditandakan sebagai tamat pengajian' },
      'LEFT_SCHOOL': { title: 'Keluar Sekolah', reason: 'LEFT_SCHOOL', message: 'Pelajar akan ditandakan sebagai keluar sekolah' },
      'REACTIVATE': { title: 'Aktifkan Semula', reason: null, message: 'Pelajar akan diaktifkan semula' }
    };

    const config = statusMap[action];
    if (!config) return;

    const result = await Swal.fire({
      title: config.title,
      text: config.message,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ya, teruskan',
      cancelButtonText: 'Batal'
    });

    if (!result.isConfirmed) return;

    setIsSubmitting(true);
    try {
      const response = await studentMasterAPI.updateEnrollmentStatus(student.id, {
        enrollYear: enrollmentData.enrollYear,
        status: action === 'REACTIVATE' ? 'ACTIVE' : 'INACTIVE',
        removalReason: config.reason
      });

      if (response.success) {
        Swal.fire('Berjaya', 'Status pendaftaran berjaya dikemaskini', 'success');
        onSuccess();
      } else {
        Swal.fire('Gagal', response.message || 'Gagal mengemaskini status', 'error');
      }
    } catch (error) {
      console.error('Update status error:', error);
      Swal.fire('Ralat', 'Ralat semasa mengemaskini status', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!formData.name.trim()) {
      Swal.fire('Maklumat Tidak Lengkap', 'Sila masukkan nama pelajar', 'warning');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await studentMasterAPI.updateStudent(student.id, {
        id: formData.id,
        name: formData.name.trim(),
        dob: formData.dob || null,
        jantina: formData.jantina || null,
        citizenStatus: formData.citizenStatus || null
      });

      if (response.success) {
        Swal.fire('Berjaya', 'Maklumat pelajar berjaya dikemaskini', 'success');
        onSuccess();
      } else {
        Swal.fire('Gagal', response.message || 'Gagal mengemaskini pelajar', 'error');
      }
    } catch (error) {
      console.error('Update student error:', error);
      Swal.fire('Ralat', 'Ralat semasa mengemaskini pelajar', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-medium" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Pelajar</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Tab Navigation */}
        <div className="modal-tabs">
          <button
            type="button"
            className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => {
              setActiveTab('info');
              setIsEditingEnrollment(false);
            }}
          >
            Maklumat Pelajar
          </button>
          <button
            type="button"
            className={`tab-button ${activeTab === 'enrollment' ? 'active' : ''}`}
            onClick={() => setActiveTab('enrollment')}
          >
            Maklumat Pendaftaran
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Student Info Tab */}
            {activeTab === 'info' && (
              <>
                {/* IC and Basic Enrollment Info (Read-only) */}
                <div className="form-section">
                  <h3>Maklumat Asas</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <label>No. KP</label>
                      <div className="info-value">{formatIC(student.ic)}</div>
                    </div>
                    <div className="info-item">
                      <label>Sekolah</label>
                      <div className="info-value">{student.schoolName || '-'}</div>
                    </div>
                    <div className="info-item">
                      <label>Tahun</label>
                      <div className="info-value">{student.tahun || '-'}</div>
                    </div>
                    <div className="info-item">
                      <label>Kelas</label>
                      <div className="info-value">{student.kelas || '-'}</div>
                    </div>
                  </div>
                </div>

                {/* Personal Information (Editable) */}
                <div className="form-section">
                  <h3>Maklumat Peribadi</h3>
                  <div className="form-row">
                    <div className="form-group full-width">
                      <label>Nama Penuh *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Nama penuh pelajar"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Tarikh Lahir</label>
                      <input
                        type="date"
                        value={formData.dob}
                        onChange={(e) => handleInputChange('dob', e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label>Jantina</label>
                      <select
                        value={formData.jantina}
                        onChange={(e) => handleInputChange('jantina', e.target.value)}
                      >
                        <option value="">Pilih Jantina</option>
                        <option value="L">Lelaki</option>
                        <option value="P">Perempuan</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Status Warganegara</label>
                      <select
                        value={formData.citizenStatus}
                        onChange={(e) => handleInputChange('citizenStatus', e.target.value)}
                      >
                        <option value="">Pilih Status</option>
                        <option value="WARGANEGARA">Warganegara</option>
                        <option value="PEMASTAUTIN TETAP">Pemastautin Tetap</option>
                        <option value="BUKAN WARGANEGARA">Bukan Warganegara</option>
                      </select>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Enrollment Tab */}
            {activeTab === 'enrollment' && (
              <div className="form-section">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h3>Maklumat Pendaftaran (Tahun {enrollmentData.enrollYear})</h3>
                  {!isEditingEnrollment && enrollmentData.status === 'ACTIVE' && (
                    <button
                      type="button"
                      className="btn-primary btn-sm"
                      onClick={() => setIsEditingEnrollment(true)}
                    >
                      Edit
                    </button>
                  )}
                </div>

                <div className="info-grid">
                  <div className="info-item">
                    <label>Status</label>
                    <div className="info-value">
                      <span className={`status-badge ${enrollmentData.status === 'ACTIVE' ? 'status-active' : 'status-inactive'}`}>
                        {enrollmentData.status === 'ACTIVE' ? 'AKTIF' : 'TIDAK AKTIF'}
                      </span>
                    </div>
                  </div>
                  <div className="info-item">
                    <label>Tahun Pendaftaran</label>
                    <div className="info-value">{enrollmentData.enrollYear || '-'}</div>
                  </div>
                </div>

                {!isEditingEnrollment ? (
                  // Read-only mode
                  <div className="info-grid">
                    <div className="info-item">
                      <label>Tahun</label>
                      <div className="info-value">{enrollmentData.tahun || '-'}</div>
                    </div>
                    <div className="info-item">
                      <label>Kelas</label>
                      <div className="info-value">{enrollmentData.kelas || '-'}</div>
                    </div>
                  </div>
                ) : (
                  // Edit mode
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Tahun *</label>
                        <select
                          value={enrollmentData.tahun}
                          onChange={(e) => handleEnrollmentChange('tahun', e.target.value)}
                          required
                        >
                          <option value="">Pilih Tahun</option>
                          {tahunsList.map(tahun => (
                            <option key={tahun} value={tahun}>{tahun}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>Kelas *</label>
                        <select
                          value={enrollmentData.kelas}
                          onChange={(e) => handleEnrollmentChange('kelas', e.target.value)}
                          required
                          disabled={!enrollmentData.tahun}
                        >
                          <option value="">Pilih Kelas</option>
                          {kelasList.map(kelas => (
                            <option key={kelas} value={kelas}>{kelas}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="form-actions" style={{ marginTop: '20px' }}>
                      <button
                        type="button"
                        className="btn-submit"
                        onClick={handleSaveEnrollment}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? 'Menyimpan...' : 'Simpan Perubahan'}
                      </button>
                      <button
                        type="button"
                        className="btn-cancel"
                        onClick={() => {
                          setIsEditingEnrollment(false);
                          setEnrollmentData({
                            enrollYear: student.enrollYear || null,
                            tahun: student.tahun || '',
                            kelas: student.kelas || '',
                            status: student.enrollmentStatus || 'ACTIVE'
                          });
                        }}
                      >
                        Batal
                      </button>
                    </div>
                  </>
                )}

                {/* Status Change Actions */}
                {enrollmentData.status === 'ACTIVE' && !isEditingEnrollment && (
                  <div className="form-section" style={{ marginTop: '30px' }}>
                    <h4>Tukar Status</h4>
                    <div className="status-actions" style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                      <button
                        type="button"
                        className="btn-warning"
                        onClick={() => handleStatusChange('TRANSFERRED')}
                        disabled={isSubmitting}
                      >
                        Transfer Keluar
                      </button>
                      <button
                        type="button"
                        className="btn-info"
                        onClick={() => handleStatusChange('GRADUATED')}
                        disabled={isSubmitting}
                      >
                        Tamat Pengajian
                      </button>
                      <button
                        type="button"
                        className="btn-danger"
                        onClick={() => handleStatusChange('LEFT_SCHOOL')}
                        disabled={isSubmitting}
                      >
                        Keluar Sekolah
                      </button>
                    </div>
                  </div>
                )}

                {/* Reactivate Button */}
                {enrollmentData.status === 'INACTIVE' && (
                  <div className="form-section" style={{ marginTop: '30px' }}>
                    <h4>Aktifkan Semula</h4>
                    <button
                      type="button"
                      className="btn-success"
                      onClick={() => handleStatusChange('REACTIVATE')}
                      disabled={isSubmitting}
                      style={{ marginTop: '15px' }}
                    >
                      Aktifkan Semula Pendaftaran
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Tutup
            </button>
            {activeTab === 'info' && (
              <button
                type="submit"
                className="btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Menyimpan...' : 'Simpan'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStudentModal;
